"use client";

import { useEffect, useRef, useState } from "react";

interface Metric {
  value: string;
  numericValue: number;
  suffix: string;
  prefix: string;
  label: string;
  sublabel: string;
}

const metrics: Metric[] = [
  {
    value: "20+",
    numericValue: 20,
    suffix: "+",
    prefix: "",
    label: "Years of Expertise",
    sublabel: "Infrastructure through executive leadership",
  },
  {
    value: "450+",
    numericValue: 450,
    suffix: "+",
    prefix: "",
    label: "Team Members Led",
    sublabel: "Full departments built from scratch",
  },
  {
    value: "3",
    numericValue: 3,
    suffix: "",
    prefix: "",
    label: "Executive Disciplines",
    sublabel: "Technology, Security, and Operations",
  },
  {
    value: "10+",
    numericValue: 10,
    suffix: "+",
    prefix: "",
    label: "Industries Served",
    sublabel: "E-commerce, fintech, media, and more",
  },
];

function AnimatedCounter({
  numericValue,
  prefix,
  suffix,
  isVisible,
}: {
  numericValue: number;
  prefix: string;
  suffix: string;
  isVisible: boolean;
}) {
  const [display, setDisplay] = useState(0);
  const animatedRef = useRef(false);

  useEffect(() => {
    if (!isVisible || animatedRef.current) return;
    animatedRef.current = true;
    const duration = 1800;
    const startTime = performance.now();

    const step = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.floor(eased * numericValue));
      if (progress < 1) requestAnimationFrame(step);
    };

    requestAnimationFrame(step);
  }, [isVisible, numericValue]);

  return (
    <span className="font-fraunces text-[clamp(2.8rem,5vw,4.5rem)] font-light text-gradient-gold leading-none">
      {prefix}
      {display}
      {suffix}
    </span>
  );
}

export default function MetricsSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="results"
      ref={sectionRef}
      aria-label="Track record"
      className="py-24 bg-[#0C0C0E] relative overflow-hidden"
    >
      {/* Background Grid */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(184,152,106,1) 1px, transparent 1px), linear-gradient(90deg, rgba(184,152,106,1) 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />

      <div className="max-w-8xl mx-auto px-6 lg:px-12 relative">
        {/* Section Label */}
        <div className="flex items-center gap-4 mb-16 reveal-hidden" data-reveal>
          <div className="w-8 h-[1px] bg-[#B8986A]" />
          <span className="text-[10px] font-bold tracking-[0.35em] uppercase text-[#B8986A]">
            By the Numbers
          </span>
        </div>

        <div className="divider-gold mb-16" />

        {/* Metrics Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-0">
          {metrics.map((metric, i) => (
            <div
              key={metric.label}
              className={`px-8 py-10 reveal-hidden delay-${i * 100 + 100} ${
                i < metrics.length - 1
                  ? "border-r border-[rgba(242,239,233,0.06)]"
                  : ""
              } ${i === 0 || i === 2 ? "border-b lg:border-b-0 border-[rgba(242,239,233,0.06)]" : ""}`}
              data-reveal
            >
              <AnimatedCounter
                numericValue={metric.numericValue}
                prefix={metric.prefix}
                suffix={metric.suffix}
                isVisible={isVisible}
              />
              <div className="mt-4 mb-1 text-[#F2EFE9] text-sm font-semibold tracking-wide">
                {metric.label}
              </div>
              <div className="text-[#6B6864] text-xs font-medium">
                {metric.sublabel}
              </div>
            </div>
          ))}
        </div>

        <div className="divider-gold mt-16" />

        {/* Industries Marquee */}
        <div className="mt-16 overflow-hidden" aria-label="Industries served" role="marquee">
          <p className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#6B6864] text-center mb-10 reveal-hidden" data-reveal>
            Industries We&apos;ve Operated In
          </p>
          <div className="relative overflow-hidden">
            <div className="marquee-track flex items-center gap-16 whitespace-nowrap">
              {[
                "E-Commerce", "Fintech", "Payments", "Streaming Media", "EdTech",
                "Cloud Services", "Enterprise SaaS", "Financial Services", "Logistics", "Retail",
                "E-Commerce", "Fintech", "Payments", "Streaming Media", "EdTech",
                "Cloud Services", "Enterprise SaaS", "Financial Services", "Logistics", "Retail",
              ].map((name, i) => (
                <span
                  key={`${name}-${i}`}
                  className="text-[#3A3A40] font-fraunces font-medium text-2xl tracking-wide select-none hover:text-[#6B6864] transition-colors duration-300"
                >
                  {name}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}